import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { safeStorage } from '../utils/safeStorage';
import { Users, Plus, Crown, UserPlus, Share2 } from 'lucide-react';
import InviteToCrewModal from './InviteToCrewModal';

function CrewsList({ currentUserId, memberUserId, currentUser, onCreateCrew, refreshKey = 0 }) {
  const [crews, setCrews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [justJoinedId, setJustJoinedId] = useState(null);
  const [justJoinedName, setJustJoinedName] = useState('');

  useEffect(() => {
    if (currentUserId || memberUserId) {
      fetchUserCrews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, memberUserId, refreshKey]); // fetchUserCrews is stable here

  useEffect(() => {
    try {
      const id = safeStorage.get('crewJustJoinedId');
      if (id) {
        setJustJoinedId(id);
        const name = crews.find(c => c.id === id)?.name || '';
        setJustJoinedName(name);
        safeStorage.remove('crewJustJoinedId');
        setTimeout(() => {
          setJustJoinedId(null);
          setJustJoinedName('');
        }, 3000);
      }
    } catch {}
  }, [refreshKey, crews]);

  const fetchUserCrews = async () => {
    try {
      setLoading(true);

      // Fetch crews where user is creator
      const { data: createdCrews, error: createdError } = await supabase
        .from('crews')
        .select('*')
        .eq('created_by', currentUserId);

      if (createdError) throw createdError;

      // Fetch crews where user is a member
      let membershipData = [];
      if (memberUserId) {
        const { data, error: membershipError } = await supabase
          .from('crew_members')
          .select('crew_id')
          .eq('user_id', memberUserId);

        if (membershipError) throw membershipError;
        membershipData = data || [];
      }

      // Get the actual crew details for memberships
      let joinedCrews = [];
      if (membershipData && membershipData.length > 0) {
        const crewIds = membershipData.map(m => m.crew_id);
        
        const { data: memberCrews, error: memberCrewsError } = await supabase
          .from('crews')
          .select('*')
          .in('id', crewIds)
          .neq('created_by', currentUserId); // Exclude crews we created (already in createdCrews)

        if (memberCrewsError) throw memberCrewsError;
        joinedCrews = memberCrews || [];
      }

      // Combine and deduplicate
      const allCrews = [...(createdCrews || []), ...joinedCrews];
      const uniqueCrews = Array.from(new Map(allCrews.map(crew => [crew.id, crew])).values());

      // Add isCreator flag
      const crewsWithRole = uniqueCrews.map(crew => ({
        ...crew,
        isCreator: crew.created_by === currentUserId
      }));

      setCrews(crewsWithRole);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching crews:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
        <div className="flex items-center gap-2 mb-4">
          <Users size={20} className="text-purple-400" />
          <h3 className="font-black text-lg text-slate-50">Your Arenas</h3>
        </div>
        <div className="text-slate-400 text-center py-4">Loading crews...</div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Users size={20} className="text-purple-400" />
            <h3 className="font-black text-lg text-slate-50">Your Arenas</h3>
            <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded-full font-bold">
              {crews.length}
            </span>
          </div>
          <div className="mt-1 text-xs text-slate-400">
            The rooms where your people are taking sides.
          </div>
        </div>
        <button
          onClick={onCreateCrew}
          className="flex items-center gap-1 bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg font-bold text-sm transition-colors"
        >
          <Plus size={16} />
          Start Arena
        </button>
      </div>

      {justJoinedId ? (
        <div className="mb-3 rounded-lg border border-emerald-400/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200 font-bold">
          ✅ Joined {justJoinedName || 'crew'}!
        </div>
      ) : null}

      {crews.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-4xl mb-3">👥</div>
          <p className="text-slate-400 mb-4">You have not joined a side room yet.</p>
          <button
            onClick={onCreateCrew}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-bold transition-colors"
          >
            Start Your First Arena
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {crews.map(crew => (
            <div
              key={crew.id}
              className={`bg-slate-900/50 rounded-lg p-4 border transition-colors ${
                crew.id === justJoinedId
                  ? 'border-emerald-400/80 shadow-[0_0_0_1px_rgba(52,211,153,0.5)]'
                  : 'border-slate-700 hover:border-purple-500'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <div className="text-3xl">{crew.emoji || '👥'}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-bold text-slate-50">{crew.name}</h4>
                      {crew.isCreator && (
                        <span className="flex items-center gap-1 text-xs bg-yellow-500/20 text-yellow-300 px-2 py-0.5 rounded-full font-bold">
                          <Crown size={12} />
                          You started this
                        </span>
                      )}
                      {!crew.isCreator && (
                        <span className="flex items-center gap-1 text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full font-bold">
                          <UserPlus size={12} />
                          You’re in
                        </span>
                      )}
                    </div>
                    {crew.description && (
                      <p className="text-sm text-slate-400 mb-2">{crew.description}</p>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span>👥 {crew.member_count || 0} voices</span>
                        {crew.total_votes > 0 && (
                          <span>🗳️ {crew.total_votes} takes cast</span>
                        )}
                      </div>
                      {crew.isCreator && (
                        <button
                          onClick={() => {
                            setSelectedCrew(crew);
                            setShowInviteModal(true);
                          }}
                          className="flex items-center gap-1 bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors"
                        >
                          <Share2 size={14} />
                          Bring People In
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && selectedCrew && (
        <InviteToCrewModal
          crew={selectedCrew}
          currentUser={currentUser}
          onClose={() => {
            setShowInviteModal(false);
            setSelectedCrew(null);
            // Refresh crews to update member count
            fetchUserCrews();
          }}
        />
      )}
    </div>
  );
}

export default CrewsList;
